import { devModeMocks } from "@api/core-api-utils";
import { faroCoreApiClient } from "@api/faro-core-api-client";
import { getTeams } from "@api/teams-api-utils";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { FaroCoreApiUtils } from "@stellar/core-api-client";
import { updateMember } from "@store/members/members-slice";
import { teamAdapter } from "@store/teams/teams-slice";
import { sentryCaptureError } from "@utils/sentry-utils";
import { canRoleComeFromTeam, isRoleRaisedByTeam } from "@utils/team-utils";
/**
 * The latest reload of each member, by workspace and member. A member can join and leave teams
 * quickly one after another, and only the answer of the latest reload may reach the store, since
 * the answers can arrive in another order than the reloads were started in.
 */
const latestReloadIdByMember = new Map();
let lastReloadId = 0;
/**
 * Loads the workspace role of a member again after they joined or left a team, and stores only
 * that role. The answer arrives later, so it is dropped when it is no longer the latest reload of
 * the member, when another workspace was selected since, or when something else changed the role
 * of the member since, e.g. the role dropdown, whose newer change wins.
 *
 * It is a refresh in the background of a change that succeeded already, so a failure is only
 * logged instead of being reported to the user.
 */
function reloadMemberRole(dispatch, getState, {
  companyId,
  memberId,
  member
}) {
  const reloadKey = `${companyId}/${memberId}`;
  const reloadId = ++lastReloadId;
  latestReloadIdByMember.set(reloadKey, reloadId);

  /** Whether no later reload of the member was started since, then forgets this one */
  function isLatestReload() {
    const isLatest = latestReloadIdByMember.get(reloadKey) === reloadId;
    if (isLatest) {
      latestReloadIdByMember.delete(reloadKey);
    }
    return isLatest;
  }

  /** Whether the role loaded for the member still applies to what is stored now */
  function canStoreRole() {
    const state = getState();
    return state.sdbCompany.selectedSdbCompanyId === companyId && state.members.entities[memberId]?.role === member.role;
  }
  faroCoreApiClient.company.getMemberDetailsInCompany({
    companyId,
    identity: member.identity
  }).then(({
    role
  }) => {
    if (isLatestReload() && canStoreRole()) {
      dispatch(updateMember({
        id: memberId,
        changes: {
          role
        }
      }));
    }
  }).catch(error => {
    if (isLatestReload()) {
      sentryCaptureError({
        error,
        title: "Failed to reload the member role after a team change"
      });
    }
  });
}

/**
 * Loads the workspace role again of the members whose role may have changed by joining or leaving
 * the team. A member gets the workspace role of a team whenever it is higher than their own, so
 * the members table would otherwise keep showing the old role.
 *
 * Members who did not join the workspace yet have no details to load.
 */
function reloadMembersWithRoleFromTeam(dispatch, getState, {
  companyId,
  teamId,
  memberIds,
  hasRoleChanged
}) {
  const state = getState();
  // The workspace role of a team is stored with the team as a workspace member
  const teamRole = state.members.entities[teamId]?.role;
  if (!teamRole) {
    return;
  }
  for (const memberId of memberIds) {
    const member = state.members.entities[memberId];
    if (member && FaroCoreApiUtils.Member.isMemberActive(member) && hasRoleChanged(teamRole, member.role)) {
      reloadMemberRole(dispatch, getState, {
        companyId,
        memberId,
        member
      });
    }
  }
}

/**
 * Fetches teams from the backend so they can be put into the store
 * @throws {HttpUtils.FetchError} if any Core API client request fails
 */
export const fetchTeams = createAsyncThunk("teams/fetchTeams", async ({
  searchText,
  memberIdentity
}, {
  getState
}) => {
  const {
    sdbCompany: {
      selectedSdbCompanyId
    },
    app: {
      isDevModeEnabled
    }
  } = getState();
  if (!selectedSdbCompanyId) {
    throw new Error("No companyId was given to search for teams");
  }
  try {
    return await getTeams({
      companyId: selectedSdbCompanyId,
      search: searchText,
      memberIdentity
    });
  } catch (error) {
    if (isDevModeEnabled) {
      return devModeMocks.teams;
    }
    throw error;
  }
}, {
  condition: ({
    searchText,
    memberIdentity
  }, {
    getState
  }) => {
    const state = getState();

    // Member-scoped fetch: only skip when the same member's teams are already
    // loaded and there is no search text to apply.
    if (memberIdentity) {
      return !(state.teams.memberTeams.status === "succeeded" && state.teams.memberTeams.memberIdentity === memberIdentity && !searchText);
    }

    // If succeeded fetching status means the data is already fetched once and no need to do it again if there is no search text
    // and there is no search text, do not fetch again
    if (state.teams.fetching.status === "succeeded" && !searchText) {
      return false;
    }
    return true;
  }
});
/**
 * Fetches the IDs of the teams a member belongs to, unless they are already fetched or being
 * fetched. They are fetched again after a failure.
 *
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const fetchTeamIdsOfMember = createAsyncThunk("teams/fetchTeamIdsOfMember", async ({
  companyId,
  memberIdentity
}) => {
  const teams = await getTeams({
    companyId,
    memberIdentity
  });
  return teams.map(team => team.id);
}, {
  condition: ({
    memberIdentity
  }, {
    getState
  }) => {
    const status = getState().teams.teamIdsByMember[memberIdentity]?.status;
    return status !== "pending" && status !== "succeeded";
  }
});

/**
 * Fetches the details of a team
 * @throws {Error} if there is no selected company ID
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const fetchTeamDetails = createAsyncThunk("teams/fetchTeamDetails", async ({
  teamId
}, {
  getState
}) => {
  const {
    sdbCompany: {
      selectedSdbCompanyId
    }
  } = getState();
  if (!selectedSdbCompanyId) {
    throw new Error("No companyId was given to fetch team details");
  }
  return await faroCoreApiClient.teams.fetchTeamById({
    companyId: selectedSdbCompanyId,
    teamId
  });
});

/**
 * Fetches the details of a team as member
 * @throws {Error} if there is no selected company ID
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const fetchTeamAsMember = createAsyncThunk("teams/fetchTeamAsMember", async ({
  teamId
}, {
  getState
}) => {
  const {
    sdbCompany: {
      selectedSdbCompanyId
    }
  } = getState();
  if (!selectedSdbCompanyId) {
    throw new Error("No companyId was given to fetch the team");
  }

  /**
   * Fetches the details of a team as member. This is the way to get all the groups and projects that the team is part of.
   * Backend will accept the team ID in fetching members route and return it in the member type
   */
  return await faroCoreApiClient.company.getMemberDetailsInCompany({
    companyId: selectedSdbCompanyId,
    identity: teamId
  });
});

/**
 * Creates a team in the backend and adds it to the store
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const createTeam = createAsyncThunk("teams/createTeam", async ({
  companyId,
  teamName,
  description
}) => {
  const team = await faroCoreApiClient.teams.createNewTeam({
    companyId,
    payload: {
      name: teamName,
      description
    }
  });
  return team;
});

/**
 * Fetches team members from the backend so they can be put into the store
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const fetchTeamMembers = createAsyncThunk("teams/fetchTeamMembers", async ({
  companyId,
  teamId,
  start,
  sortBy,
  search
}) => {
  const result = await faroCoreApiClient.teams.fetchTeamMembers({
    companyId,
    teamId,
    start,
    sortBy,
    search
  });
  return {
    teamId,
    members: result.data,
    next: result.page.next,
    totalCount: result.page.total
  };
}, {
  condition: ({
    start,
    sortBy,
    search
  }, api) => {
    const state = api.getState();
    if (start) {
      return true;
    }
    const isSameSortBy = state.teams.selectedTeam.membersSortBy === (sortBy ?? null);
    const isSameSearch = state.teams.selectedTeam.membersSearch === (search ?? null);

    // Skip fetching if we already have the data and not fetching again
    if (state.teams.fetching.isFetchingTeamMembers === "succeeded" && isSameSortBy && isSameSearch) {
      return false;
    }
    return true;
  }
});

/**
 * Invite members to a team
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const addMembersToTeam = createAsyncThunk("teams/addMembersToTeam", async ({
  companyId,
  teamId,
  members
}, {
  dispatch,
  getState
}) => {
  const addMembersResponse = await faroCoreApiClient.teams.addMembersToTeam({
    companyId,
    teamId,
    payload: {
      members
    }
  });
  reloadMembersWithRoleFromTeam(dispatch, getState, {
    companyId,
    teamId,
    memberIds: addMembersResponse.data.map(({
      userResponse
    }) => FaroCoreApiUtils.Member.getMemberId(userResponse)),
    // Joining only changes the role when the team gives a higher one
    hasRoleChanged: isRoleRaisedByTeam
  });
  return {
    ...addMembersResponse,
    teamId
  };
});
/**
 * Update team details
 * @throws {Error} if there is no selected company ID or team ID or if team is not found in the store
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const updateTeamDetails = createAsyncThunk("teams/updateTeamDetails", async ({
  teamId,
  payload
}, {
  getState
}) => {
  const {
    teams,
    sdbCompany
  } = getState();

  /**
   * The ID of the team that is going to be updated.
   * If teamId is not provided, the selectedTeamId from store is selected
   */
  const updatingTeamId = teamId || teams.selectedTeam.id;
  if (!sdbCompany.selectedSdbCompanyId) {
    throw new Error("No companyId exist to updateTeamDetails");
  }
  if (!updatingTeamId) {
    throw new Error("No teamId exist to updateTeamDetails");
  }
  const team = teamAdapter.getSelectors().selectById(teams, updatingTeamId);
  if (!team) {
    throw new Error("Team not found");
  }
  const updatedTeam = await faroCoreApiClient.teams.updateTeam({
    companyId: sdbCompany.selectedSdbCompanyId,
    teamId: updatingTeamId,
    payload
  });
  return updatedTeam;
});

/**
 * Remove a member from the team using the backend,
 * and then removes it from team in the store as well
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const removeMembersFromTeam = createAsyncThunk("teams/removeMembersFromTeam", async ({
  companyId,
  teamId,
  memberIds
}, {
  dispatch,
  getState
}) => {
  await faroCoreApiClient.teams.removeMembersFromTeam({
    companyId,
    teamId,
    payload: {
      members: memberIds
    }
  });
  reloadMembersWithRoleFromTeam(dispatch, getState, {
    companyId,
    teamId,
    memberIds,
    // Leaving can lower the shown role whenever it may have come from the team
    hasRoleChanged: canRoleComeFromTeam
  });
  return {
    teamId,
    companyId,
    memberIds
  };
});

/**
 * Delete a team
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const deleteTeam = createAsyncThunk("teams/deleteTeam", async ({
  companyId,
  teamId
}) => {
  await faroCoreApiClient.teams.deleteTeam({
    companyId,
    teamId
  });
  return teamId;
});